import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";

/**
 * The server-owned Summary report-structure registry (#639's API, #640's
 * surface).
 *
 * Aliases into the generated contract, the `hooks.ts` and `library.ts` rule:
 * the contract is the definition, and a shape that drifts from it stops
 * compiling rather than stops working. Nothing in this module or in anything
 * that consumes it declares a layer, an ownership class, a report section or a
 * typography constraint of its own — #639's own preamble is explicit that
 * clients DISCOVER the structure and nobody authors it free-form, and a
 * browser-side constant would be exactly the second manifest that forbids.
 */
export type SummaryStructureRegistry =
  components["schemas"]["SummaryStructureRegistry"];
export type SummaryLayerContract =
  components["schemas"]["SummaryLayerContract"];
export type SummaryLayer = components["schemas"]["SummaryLayer"];
export type SummaryOwnership = components["schemas"]["SummaryOwnership"];
export type SummaryLayerVersioning =
  components["schemas"]["SummaryLayerVersioning"];
export type SummaryLayerConcurrency =
  components["schemas"]["SummaryLayerConcurrency"];
export type SummaryValueCategory =
  components["schemas"]["SummaryValueCategory"];
export type SummaryValueCategoryId =
  components["schemas"]["SummaryValueCategoryId"];
export type SummaryCategoryAuthoring =
  components["schemas"]["SummaryCategoryAuthoring"];
export type SummaryReportSection =
  components["schemas"]["SummaryReportSection"];
export type SummaryReportSectionSlug =
  components["schemas"]["SummaryReportSectionSlug"];
export type SummarySectionMapping =
  components["schemas"]["SummarySectionMapping"];
export type SummaryMappingState = components["schemas"]["SummaryMappingState"];
export type SummaryMappingAuthority =
  components["schemas"]["SummaryMappingAuthority"];
export type SummaryTypographyDeclaration =
  components["schemas"]["SummaryTypographyDeclaration"];
export type SummarySectionPresentation =
  components["schemas"]["SummarySectionPresentation"];

/**
 * The structure version THIS build renders, sent as `expectedRegistryVersion`
 * on every read.
 *
 * #639 publishes the read as a conditional one and says what the condition is
 * for: "a client built against another structure is refused at the boundary
 * … rather than rendering a report structure this deployment does not state".
 * That refusal is what this constant buys. The vocabularies this surface
 * switches on — `SummaryOwnership`, `SummaryValueCategoryId`,
 * `SummaryMappingState` — are closed enums whose label maps are written
 * `satisfies Record<…>` precisely so a widened contract stops the build; the
 * pin closes the other half of that guarantee, where the CONTRACT is unchanged
 * but the DEPLOYMENT serves a structure this build has never seen. Without it
 * a v3 registry would render through v2 label maps and a missing member would
 * reach a clinician as a blank cell.
 *
 * It is deliberately a literal rather than anything derived. The registry
 * version moves when the structure or mapping moves — a section added, an
 * ownership reassigned, a mapping established — which is exactly the set of
 * changes that need a human to re-read this surface's copy. Bumping it is
 * therefore a deliberate act, and the surface refusing until someone performs
 * it is the intended cost.
 */
export const SUMMARY_STRUCTURE_CLIENT_VERSION = "v2";

/**
 * The registry, asserted against the version this build renders.
 *
 * Cacheable and shared on the same terms as the placement registry and the
 * part manifest next door in `library.ts`: the key names no project and no
 * asset, because the document describes the SERVICE's Summary policy and moves
 * with a deployment rather than with anything a clinician publishes. A Project
 * surface reading it therefore observes one cache entry, and an adoption write
 * has no claim to have changed it.
 */
export function useSummaryStructureQuery(): UseQueryResult<SummaryStructureRegistry> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.librarySummaryStructure,
    async queryFn() {
      return unwrap(
        await client.GET("/v1/library/summary-report-structure", {
          params: {
            query: {
              expectedRegistryVersion: SUMMARY_STRUCTURE_CLIENT_VERSION,
            },
          },
        }),
      );
    },
  });
}

/**
 * What went wrong reading the registry, as the states this surface must tell
 * apart — `classifySectionFailure`'s shape and its discipline.
 *
 * The member that does not exist elsewhere is `mismatch`. Every other 409 in
 * this application means "someone moved first, re-read and resend"; this one
 * means the deployment serves a structure this build cannot render, there is
 * nothing to resend, and the recovery is a newer browser build rather than a
 * newer request. Keeping it distinct is what lets the surface say that instead
 * of offering a retry that cannot succeed.
 */
export type SummaryStructureFailure =
  /**
   * 409 `summary_structure_version_mismatch`. `servedVersion` is the
   * deployment's own `currentSummaryStructureVersion`, which the contract
   * attaches to this code as a flat envelope field — `null` when the envelope
   * omitted it, never guessed, because "which structure this deployment
   * serves" is precisely the fact the browser has no other way to learn.
   */
  | { kind: "mismatch"; servedVersion: string | null }
  /** 401/403 — the session does not hold `library.read`. */
  | { kind: "denied" }
  /** 501 — the registry is not built in this deployment. Terminal, no retry. */
  | { kind: "unavailable" }
  /** 502/503/504 — the service could not build the document. Retryable. */
  | { kind: "upstream" }
  /** Anything else, carrying the machine-readable code and request id only. */
  | { kind: "error"; code: string; requestId: string | undefined };

export function classifySummaryStructureFailure(
  error: unknown,
): SummaryStructureFailure {
  if (error instanceof ApiError) {
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (error.isForbidden || error.isUnauthenticated) {
      return { kind: "denied" };
    }
    if (
      error.isConflict &&
      error.code === "summary_structure_version_mismatch"
    ) {
      const served = error.envelope["currentSummaryStructureVersion"];
      return {
        kind: "mismatch",
        servedVersion: typeof served === "string" ? served : null,
      };
    }
    if (error.status === 502 || error.status === 503 || error.status === 504) {
      return { kind: "upstream" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  // A thrown non-ApiError is a network or runtime failure: no status, no code.
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}
